import { useCallback, useEffect, useState } from 'react';
import * as api from '../api.js';
import { GameFrame } from './GameFrame.js';

const BAND_NAMES: Record<string, string> = {
  sprout: 'Little Studio',
  builder: 'Big Studio',
  engineer: 'Engineers',
};

function Stars({
  value,
  onPick,
}: {
  value: number | null;
  onPick?: (n: number) => void;
}) {
  return (
    <span className="stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!onPick}
          className={n <= (value ?? 0) ? 'star star--on' : 'star'}
          onClick={() => onPick?.(n)}
          aria-label={`${n} stars`}
        >
          ★
        </button>
      ))}
    </span>
  );
}

type Props = {
  gamesOrigin: string;
  onRemixed: (buildId: string, gameUrl: string) => void;
};

export function Arcade({ gamesOrigin, onRemixed }: Props) {
  const [games, setGames] = useState<api.ArcadeGame[]>([]);
  const [board, setBoard] = useState<api.Leaderboard | null>(null);
  const [playing, setPlaying] = useState<api.ArcadeGame | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [a, l] = await Promise.all([api.getArcade(), api.getLeaderboard()]);
    setGames(a.games);
    setBoard(l);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const open = async (game: api.ArcadeGame) => {
    setPlaying(game);
    // Counting the play is what pays the creator, so it happens on open.
    await api.playGame(game.id).catch(() => {});
  };

  return (
    <section className="arcade">
      {playing && (
        <div className="arcade__player">
          <div className="arcade__playerbar">
            <strong>{playing.title}</strong>
            <span>by {playing.ownerName}</span>
            <button onClick={() => { setPlaying(null); void refresh(); }}>Close</button>
          </div>
          <GameFrame gameUrl={`${gamesOrigin}/g/${playing.buildId}`} />
          {!playing.isMine && (
            <div className="arcade__rate">
              <span>What did you think?</span>
              <Stars
                value={playing.myRating}
                onPick={async (n) => {
                  await api.rateGame(playing.id, n);
                  setPlaying({ ...playing, myRating: n });
                  void refresh();
                }}
              />
            </div>
          )}
        </div>
      )}

      <h2>The Arcade</h2>
      {note && <p className="problem">{note}</p>}

      <div className="arcade__grid">
        {games.map((game) => (
          <article key={game.id} className="arcade__card">
            <h3>{game.title}</h3>
            <p className="arcade__by">
              by {game.isMine ? 'you' : game.ownerName}
              {game.remixOf && <span className="tag">remix</span>}
            </p>
            <p className="arcade__stats">
              {game.plays} {game.plays === 1 ? 'play' : 'plays'}
              {game.averageRating !== null && <> · {game.averageRating}★</>}
            </p>
            <div className="arcade__actions">
              <button onClick={() => void open(game)}>Play</button>
              {!game.isMine && (
                <button
                  className="quiet"
                  onClick={async () => {
                    const result = await api.remixGame(game.id);
                    if (result.ok) {
                      onRemixed(result.buildId, result.gameUrl);
                    } else {
                      setNote(result.childMessage);
                    }
                  }}
                >
                  Remix it
                </button>
              )}
            </div>
          </article>
        ))}
        {games.length === 0 && <p>Nothing here yet. Make something!</p>}
      </div>

      {board && board.boards.length > 0 && (
        <>
          <h2>This week</h2>
          {board.boards.map((b) => (
            <div key={b.band} className="board">
              <h3>{BAND_NAMES[b.band] ?? b.band}</h3>
              <ol>
                {b.entries.map((e) => (
                  <li key={e.kidId}>
                    <span className="board__rank">#{e.rank}</span>
                    <span className="board__name">{e.name}</span>
                    <span className="board__detail">
                      {e.gamesShipped} shipped · {e.playsReceived} plays
                      {e.remixesReceived > 0 && ` · ${e.remixesReceived} remixed`}
                    </span>
                    <span className="board__points">{e.points}</span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
          {board.badges.length > 0 && (
            <div className="badges">
              {board.badges.map((badge) => {
                const who = board.boards
                  .flatMap((b) => b.entries)
                  .find((e) => e.kidId === badge.kidId);
                return (
                  <span key={badge.id} className="badge">
                    <strong>{badge.label}</strong>
                    {who && <em>{who.name}</em>}
                  </span>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
}
